import type { Logger } from "@expert/logging";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { getFullStoryUrl, trackFullStoryEvent } from "@expert/monitoring";
import { Alert, Modal, Stack } from "@mantine/core";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type { PartialFeedbackPayload } from "../../types";

import { useFeedbackContext } from "../../context";
import { sendFeedback } from "../../sendFeedback";
import { useFeedbackDraftStore } from "../../store";
import classes from "../../styles.module.css";
import { getTemplateFields, getTitle } from "../../types";
import { FeedbackSuccess } from "../success-animation";
import { FeedbackActions } from "./FeedbackActions";
import { FreeTextFeedback } from "./FreeTextFeedback";
import { NoTextFeedback } from "./NoTextFeedback";

interface FeedbackModalProps {
    isOpen: boolean;
    logger: Logger;
    onSubmit?: VoidFunction;
    onClose: VoidFunction;
    /** @deprecated Submission failures now surface as an inline alert inside the modal. */
    onError?: VoidFunction;
    feedbackPayload: PartialFeedbackPayload;
    isTraining?: boolean;
}
const TRANSITION_DURATION = 300; // TODO: Import this from theme constants when ea standalone moves over

export function FeedbackModal({
    isOpen,
    logger: loggerProp,
    onClose: onCloseProp,
    onSubmit: onSubmitProp,
    feedbackPayload,
}: FeedbackModalProps) {
    const { getExpertFeedbackUrl } = useFeedbackContext();
    const { resetDraft, draft } = useFeedbackDraftStore();
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const timeoutRef = useRef<number | undefined>(undefined);
    const { dispatcher } = useReactAnalytics();
    const logger = loggerProp.child({ module: "Feedback" });
    const { feedbackType } = feedbackPayload;

    const onSubmit = async () => {
        try {
            setIsLoading(true);
            setHasError(false);
            let fsUrl: string | undefined;
            try {
                fsUrl = await getFullStoryUrl();
            } catch (error) {
                logger.warn({ error }, "Error getting FullStory URL for feedback");
            }
            const fields = getTemplateFields(draft.category);
            let text = draft.description;
            if (fields && draft.templateResponses) {
                text = fields
                    .map((field) => `${field}\n${draft.templateResponses?.[field]?.trim() || "(no response)"}`)
                    .join("\n\n");
            }

            await sendFeedback(getExpertFeedbackUrl(), {
                ...feedbackPayload,
                extraData: { ...feedbackPayload.extraData, "FullStory URL": fsUrl },
                text,
                category: draft.category,
            });
            setIsLoading(false);
            onSubmitProp?.();
            resetDraft();
            logger.info("Feedback submitted");
            trackFullStoryEvent("FeedbackSubmitted", {});
            setIsSuccess(true);
        } catch (error) {
            logger.warn({ error }, "Error sending feedback");
            setIsLoading(false);
            setHasError(true);
        }
    };

    const onClear = () => {
        resetDraft();
        void dispatcher.dispatchAction("Click", "ClearFeedback", {});
        logger.info("Feedback cleared");
    };

    const onClose = () => {
        void dispatcher.dispatchAction("Click", "CloseFeedback", {});
        if (draft.description || draft.templateResponses) {
            void dispatcher.dispatchAction("Click", "SaveFeedback", {});
            logger.info("Feedback saved");
        }
        onCloseProp();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setIsSuccess(false);
            setHasError(false);
        }, TRANSITION_DURATION) as unknown as number;
    };

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    let FeedbackComponent: ReactNode;
    switch (feedbackType) {
        case "FreeTextFeedback":
            FeedbackComponent = <FreeTextFeedback isLoading={isLoading} isTraining={feedbackPayload?.isTraining} />;
            break;
        case "NoTextFeedback":
            FeedbackComponent = (
                <NoTextFeedback
                    isLoading={isLoading}
                    channel={feedbackPayload?.channel}
                    isTraining={feedbackPayload?.isTraining}
                />
            );
            break;
    }

    return (
        <Modal
            zIndex="var(--mantine-priority-highest)"
            opened={isOpen}
            onClose={onClose}
            title={getTitle(isSuccess, feedbackType, feedbackPayload?.channel)}
            size="36rem"
            centered
            className={classes.modal}
            styles={{ content: { height: "auto" } }}
        >
            {isSuccess ? (
                <FeedbackSuccess />
            ) : (
                <Stack>
                    {hasError && (
                        <Alert color="red" variant="light" data-testid="feedback-error">
                            Oops, there was an issue sending feedback. Try again—we saved your progress.
                        </Alert>
                    )}
                    {FeedbackComponent}
                    <FeedbackActions onClear={onClear} onSubmit={() => void onSubmit()} isLoading={isLoading} />
                </Stack>
            )}
        </Modal>
    );
}
